// Delete a receipt, with the Digital Waste Tracking policy.
//
// Defra has no cancel. For a receipt Defra holds, deleting it here does NOT remove
// it from Defra, so the dialog says so and asks for a mandatory reason (the server
// enforces both; it also refuses while a Defra submission is unresolved). A receipt
// Defra never accepted is deleted the normal way.

import { useEffect, useState } from 'react';
import { ConfirmModal } from '../../../components/ui/ConfirmModal/ConfirmModal';
import { fetchDeletePolicy, type DwtDeletePolicy } from '../../../api/dwt';
import { useToast } from '../../../hooks/useToast';
import { useDeleteReceipt } from '../hooks/useReceiptMutations';

export interface DeleteReceiptDialogProps {
  receipt: { enc_id: string; unique_id: string; customer_name: string } | null;
  /** Digital Waste Tracking is on for this admin: ask the server whether a reason is needed. */
  dwtOn: boolean;
  onClose: () => void;
  onDeleted: () => void;
}

export function DeleteReceiptDialog({ receipt, dwtOn, onClose, onDeleted }: DeleteReceiptDialogProps): JSX.Element {
  const toast = useToast();
  const remove = useDeleteReceipt();
  const [policy, setPolicy] = useState<DwtDeletePolicy | null>(null);
  const [reason, setReason] = useState('');

  const encId = receipt?.enc_id ?? null;
  useEffect(() => {
    setPolicy(null);
    setReason('');
    if (encId === null || !dwtOn) return;
    let cancelled = false;
    fetchDeletePolicy(encId)
      .then((p) => {
        if (!cancelled) setPolicy(p);
      })
      .catch(() => {
        if (!cancelled) setPolicy(null);
      });
    return () => {
      cancelled = true;
    };
  }, [encId, dwtOn]);

  function close() {
    setPolicy(null);
    setReason('');
    onClose();
  }

  return (
    <ConfirmModal
      open={receipt !== null}
      title="Delete receipt?"
      message={
        receipt === null ? (
          ''
        ) : policy?.reason_required ? (
          <div>
            <p style={{ marginTop: 0 }}>
              Receipt {receipt.unique_id} for {receipt.customer_name} was reported to Defra
              {policy.waste_tracking_id ? ` (Waste Tracking ID ${policy.waste_tracking_id})` : ''}. Defra has no way to
              cancel a report, so deleting it here does <strong>not</strong> remove it from Defra. You will need to correct
              or withdraw it in Defra’s service. It stays on the Digital Waste Tracking dashboard until you do.
            </p>
            <label htmlFor="delete-reason">Reason for deleting (required)</label>
            <textarea
              id="delete-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              maxLength={500}
              style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--color-border)', borderRadius: 6 }}
            />
          </div>
        ) : policy?.kind === 'unresolved' ? (
          `Receipt ${receipt.unique_id} has a Defra submission that is still in progress or whose outcome is unknown. It cannot be deleted until an admin resolves it on the Digital Waste Tracking page.`
        ) : (
          `Delete receipt ${receipt.unique_id} for ${receipt.customer_name}? This cannot be undone.`
        )
      }
      confirmLabel="Delete"
      cancelLabel="Cancel"
      variant="danger"
      loading={remove.isPending}
      onCancel={close}
      onConfirm={() => {
        if (receipt === null) return;
        if (policy?.reason_required && reason.trim().length < 5) {
          toast.error('Enter a reason (at least 5 characters).');
          return;
        }
        remove.mutate(policy?.reason_required ? { encId: receipt.enc_id, reason: reason.trim() } : receipt.enc_id, {
          onSuccess: () => {
            close();
            onDeleted();
          },
        });
      }}
    />
  );
}
